import type { RulebookDisplayedSnapshot } from "@next-platform/client-common-core";
import { useTranslation } from "@next-platform/client-common-core";
import formatMeterButtonValue from "@next-platform/client-common-core/rulebook/hud/formatMeterButtonValue";
import type { HudTileText } from "@next-platform/client-common-core/rulebook/hud/hudTileText";
import { IconSize } from "@next-platform/client-common-spicy-ui";
import type { PublicMeterView } from "@next-platform/packages-ts-types";
import React, { memo } from "react";

import RulebookMeterIcon from "@/components/Rulebook/RulebookMeterIcon";
import { useMeterColor } from "@/hooks";

import { HUD_MUTED, HUD_STRIP_BG, HUD_TERTIARY } from "./hudClassNames";
import RulebookMeterEffects from "./RulebookMeterEffects";
import RulebookMeterGauge from "./RulebookMeterGauge";
import useMeterMove from "./useMeterMove";

type RulebookMeterTileProps = {
  meter: PublicMeterView;
  value: number;
  text: HudTileText;
  snapshot: RulebookDisplayedSnapshot;
  isArrived: boolean;
  /** `tile`: desktop score panel (uppercase label, large value, glowing bar); `grid`: mobile 2×2 row. */
  variant: "tile" | "grid";
  testIdPrefix: string;
};

/** One meter in the top panel, with the change glow and floating "+N" when its snapshot just arrived. */
const RulebookMeterTile = ({ meter, value, text, snapshot, isArrived, variant, testIdPrefix }: RulebookMeterTileProps) => {
  const { t } = useTranslation();
  const meterColor = useMeterColor(meter.color);
  const spokenValue = formatMeterButtonValue({ meter, snapshot, t });
  const ariaLabel =
    spokenValue !== undefined
      ? t("chat:rulebook.hud.meterButton", { label: meter.label, value: spokenValue, defaultValue: "{{label}}: {{value}}" })
      : t("chat:rulebook.hud.meterButtonNoValue", { label: meter.label, defaultValue: "{{label}}" });
  const move = useMeterMove({ meter, color: meterColor, snapshot, isArrived });
  const iconKey = `icon-${move?.iconAnimation ? snapshot.messageId : "still"}`;
  const effects = <RulebookMeterEffects move={move} snapshotId={snapshot.messageId} meterId={meter.id} testIdPrefix={testIdPrefix} />;

  if (variant === "tile") {
    return (
      <div
        data-testid={`${testIdPrefix}-Meter-${meter.id}`}
        role="group"
        aria-label={ariaLabel}
        className={`relative flex min-w-0 flex-col gap-1.5 rounded-[10px] px-2.5 py-1.5 ${HUD_STRIP_BG}`}
      >
        {effects}
        <div className="flex min-w-0 items-center gap-1.5">
          <span key={iconKey} className={`inline-flex flex-none ${move?.iconAnimation ?? ""}`}>
            <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={meterColor} size={IconSize.XSMALL} />
          </span>
          <span className={`min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-[0.1em] ${HUD_MUTED}`}>{meter.label}</span>
        </div>
        <span
          className="truncate whitespace-nowrap text-[17px] font-extrabold leading-none tabular-nums"
          data-testid={`${testIdPrefix}-Value-${meter.id}`}
        >
          {text.value}
        </span>
        <RulebookMeterGauge meter={meter} value={value} color={meterColor} variant="segments" testId={`${testIdPrefix}-Gauge-${meter.id}`} />
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-Meter-${meter.id}`} role="group" aria-label={ariaLabel} className="relative flex min-w-0 items-center gap-2">
      {effects}
      <span key={iconKey} className={`inline-flex flex-none ${move?.iconAnimation ?? ""}`}>
        <RulebookMeterIcon icon={meter.icon} meterType={meter.type} color={meterColor} />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2 text-[11px] leading-none">
          <span className={`truncate ${HUD_TERTIARY}`}>{meter.label}</span>
          <span className="whitespace-nowrap font-semibold" data-testid={`${testIdPrefix}-Value-${meter.id}`}>
            {text.value}
          </span>
        </div>
        <RulebookMeterGauge meter={meter} value={value} color={meterColor} variant="thin" testId={`${testIdPrefix}-Gauge-${meter.id}`} />
      </div>
    </div>
  );
};

export default memo(RulebookMeterTile);
